import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
} from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { SCRIPT_GENERATION_FLOW_NAME } from "../../../constants/script-generation"

export type ScriptGenerationGateMode = "all_ineligible" | "partial" | "unknown"

export interface ScriptGenerationGateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: ScriptGenerationGateMode
  selectedCount: number
  eligibleCount: number
  ineligibleCount: number
  unknownCount: number
  ineligibleDatasets: string[]
  onSwitchToTopics: () => void
  onGenerateEligible: () => void
}

export function ScriptGenerationGateDialog({
  open,
  onOpenChange,
  mode,
  selectedCount,
  eligibleCount,
  ineligibleCount,
  unknownCount,
  ineligibleDatasets,
  onSwitchToTopics,
  onGenerateEligible,
}: ScriptGenerationGateDialogProps) {
  const { t } = useTranslation()
  const datasetLabel = SCRIPT_GENERATION_FLOW_NAME

  const hintKey =
    mode === "unknown"
      ? "opportunities.bulk.scriptsGate.hintUnknown"
      : mode === "all_ineligible"
        ? "opportunities.bulk.scriptsGate.hintAllIneligible"
        : "opportunities.bulk.scriptsGate.hintPartial"

  const readyPct =
    selectedCount > 0 ? Math.round((eligibleCount / selectedCount) * 100) : 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("opportunities.bulk.scriptsGate.title", { dataset: datasetLabel })}</DialogTitle>
          <DialogDescription>
            {t("opportunities.bulk.scriptsGate.lead", { dataset: datasetLabel })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <SelectionBreakdown
            datasetLabel={datasetLabel}
            selectedCount={selectedCount}
            eligibleCount={eligibleCount}
            ineligibleCount={ineligibleCount}
            unknownCount={unknownCount}
            ineligibleDatasets={ineligibleDatasets}
            readyPct={readyPct}
            t={t}
          />
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t(hintKey, { dataset: datasetLabel })}
          </p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          {mode === "partial" && eligibleCount > 0 && (
            <Button variant="outline" onClick={onGenerateEligible}>
              {t("opportunities.bulk.scriptsGate.generateEligible", { count: eligibleCount })}
            </Button>
          )}
          <Button onClick={onSwitchToTopics}>
            {t("opportunities.bulk.scriptsGate.switchToTopics", { dataset: datasetLabel })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SelectionBreakdown({
  datasetLabel,
  selectedCount,
  eligibleCount,
  ineligibleCount,
  unknownCount,
  ineligibleDatasets,
  readyPct,
  t,
}: {
  datasetLabel: string
  selectedCount: number
  eligibleCount: number
  ineligibleCount: number
  unknownCount: number
  ineligibleDatasets: string[]
  readyPct: number
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  const showReadyBar = selectedCount > 0 && eligibleCount > 0 && ineligibleCount > 0

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="mb-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{t("opportunities.bulk.scriptsGate.selectedTotal", { count: selectedCount })}</span>
        {showReadyBar && (
          <span className="tabular-nums">
            {t("opportunities.bulk.scriptsGate.readyShare", { pct: readyPct })}
          </span>
        )}
      </div>

      {showReadyBar && (
        <div
          className="mb-3 h-1.5 overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label={t("opportunities.bulk.scriptsGate.readyShare", { pct: readyPct })}
        >
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${readyPct}%` }}
          />
        </div>
      )}

      <ul className="space-y-2">
        {eligibleCount > 0 && (
          <li>
            <BreakdownRow
              icon="check-circle"
              iconClassName="text-status-success"
              label={datasetLabel}
              count={eligibleCount}
              variant="ready"
            />
          </li>
        )}
        {ineligibleCount > 0 && (
          <li className="space-y-1.5">
            <BreakdownRow
              icon="x-circle"
              iconClassName="text-muted-foreground"
              label={t("opportunities.bulk.scriptsGate.otherDatasets")}
              count={ineligibleCount}
              variant="blocked"
            />
            {ineligibleDatasets.length > 0 && (
              <div className="flex flex-wrap gap-1 pl-6">
                {ineligibleDatasets.map((name) => (
                  <Badge key={name} variant="secondary" className="max-w-full truncate font-normal">
                    {name}
                  </Badge>
                ))}
              </div>
            )}
          </li>
        )}
        {unknownCount > 0 && (
          <li>
            <BreakdownRow
              icon="alert-circle"
              iconClassName="text-warning"
              label={t("opportunities.bulk.scriptsGate.notLoaded")}
              count={unknownCount}
              variant="unknown"
            />
          </li>
        )}
      </ul>
    </div>
  )
}

function BreakdownRow({
  icon,
  iconClassName,
  label,
  count,
  variant,
}: {
  icon: "check-circle" | "x-circle" | "alert-circle"
  iconClassName: string
  label: string
  count: number
  variant: "ready" | "blocked" | "unknown"
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm",
        variant === "ready" && "bg-status-success/10",
        variant === "blocked" && "bg-muted/60",
        variant === "unknown" && "bg-warning/10",
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        <Icon name={icon} size="xs" className={cn("shrink-0", iconClassName)} />
        <span className="truncate font-medium text-foreground">{label}</span>
      </span>
      <span className="shrink-0 tabular-nums font-semibold text-foreground">{count}</span>
    </div>
  )
}
